import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, Observable, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { CreateDefinitionValueDto, CreateMachineManufacturerModelDto, DefinitionModelDto, DeleteDefinitionValueDto, DeleteMachineManufacturerModelDto, } from '../../models/definitions/definition-model-dto';
import { EnumValue } from '../../models/definitions/definition-key-value-models';
import { SnackbarService } from '../error/snackbar.service';
import { DefinitionType } from '../../enums/definition-type';
import { SnackbarConfig, SnackbarType, SnackbarPosition } from '../../enums/snackbar-config';
import { MachineManufacturerModelDto } from '../../models/definitions/machine-manufacturer-model-dto';
import { browserOnly } from './browser-only.util';
@Injectable({
  providedIn: 'root'
})
export class DefinitionApiService {
  private apiUrl = environment.apiBaseUrl;
  errorResponse: string = "";

  constructor(
    private http: HttpClient,
    private snackBarService: SnackbarService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  getHarvesterHeads(): Observable<DefinitionModelDto[]> {
    return browserOnly<DefinitionModelDto[]>(this.platformId, [], () => this.http.get<DefinitionModelDto[]>(`${this.apiUrl}/harvesterheads`).pipe(catchError(error => this.snackBarService.handleApiError(error))));
  }

  getMachineManufacturers(): Observable<DefinitionModelDto[]> {
    return browserOnly<DefinitionModelDto[]>(this.platformId, [], () => this.http.get<DefinitionModelDto[]>(`${this.apiUrl}/machinemanufacturers`).pipe(catchError(error => this.snackBarService.handleApiError(error))));
  }

  getMachineManufacturerModels(manufacturerId: number): Observable<MachineManufacturerModelDto[]> {
    return browserOnly<MachineManufacturerModelDto[]>(this.platformId, [], () => this.http.get<MachineManufacturerModelDto[]>(`${this.apiUrl}/machinemanufacturermodels/${manufacturerId}`).pipe(catchError(error => this.snackBarService.handleApiError(error))));
  }

  getMachineComputers(): Observable<DefinitionModelDto[]> {
    return browserOnly<DefinitionModelDto[]>(this.platformId, [], () => this.http.get<DefinitionModelDto[]>(`${this.apiUrl}/machinecomputers/`).pipe(catchError(error => this.snackBarService.handleApiError(error))));
  }

  getMachinePerformanceTypes(): Observable<EnumValue[]> {
    return browserOnly<EnumValue[]>(this.platformId, [], () => this.http.get<EnumValue[]>(`${this.apiUrl}/machineperformancetypes`).pipe(catchError(error => this.snackBarService.handleApiError(error))));
  }

  updateDefinitionValue(definitionType: DefinitionType, updateDefinitionModel: DefinitionModelDto): Observable<DefinitionModelDto> {
    return browserOnly<DefinitionModelDto>(this.platformId, {} as DefinitionModelDto, () => this.http.put<never>(`${this.apiUrl}/definition/update/${definitionType}/${updateDefinitionModel.id}`, updateDefinitionModel, {
      headers: new HttpHeaders({
        "Content-Type": "application/json"
      })
    }).pipe(tap(() => {
      this.snackBarService.showResponse("Uppdatering genomförd.", SnackbarConfig.combine(SnackbarType.SUCCESS, SnackbarPosition.BOTTOM))
    }),
      catchError(error => this.snackBarService.handleApiError(error))
    ))
  }

  updateMachineManufacturerModelValue(updateModelModel: MachineManufacturerModelDto): Observable<MachineManufacturerModelDto> {
    return browserOnly<MachineManufacturerModelDto>(this.platformId, {} as MachineManufacturerModelDto, () => this.http.put<never>(`${this.apiUrl}/machinemanufacturermodel/update/${updateModelModel.id}`, updateModelModel, {
      headers: new HttpHeaders({
        "Content-Type": "application/json"
      })
    }).pipe(tap(() => {
      this.snackBarService.showResponse("Uppdatering genomförd.", SnackbarConfig.combine(SnackbarType.SUCCESS, SnackbarPosition.BOTTOM))
    }),
      catchError(error => this.snackBarService.handleApiError(error))
    ))
  }

  createDefinitionValue(definitionType: DefinitionType, saveDefinitionModel: CreateDefinitionValueDto): Observable<DefinitionModelDto> {
    return browserOnly<DefinitionModelDto>(this.platformId, {} as DefinitionModelDto, () => this.http.post<never>(`${this.apiUrl}/definition/create/${definitionType}`, saveDefinitionModel, {
      headers: new HttpHeaders({
        "Content-Type": "application/json"
      })
    }).pipe(tap(() => {
      this.snackBarService.showResponse("Definitionsvärde skapat.", SnackbarConfig.combine(SnackbarType.SUCCESS, SnackbarPosition.BOTTOM))
    }),
      catchError(error => this.snackBarService.handleApiError(error))
    ))
  }

  createMachineManufacturerModel(machineManufacturerId: number, saveManufacturerModelModel: CreateMachineManufacturerModelDto): Observable<MachineManufacturerModelDto> {
    return browserOnly<MachineManufacturerModelDto>(this.platformId, {} as MachineManufacturerModelDto, () => this.http.post<never>(`${this.apiUrl}/machinemanufacturermodel/create/${machineManufacturerId}`, saveManufacturerModelModel, {
      headers: new HttpHeaders({
        "Content-Type": "application/json"
      })
    }).pipe(tap(() => {
      this.snackBarService.showResponse("Definitionsvärde skapat.", SnackbarConfig.combine(SnackbarType.SUCCESS, SnackbarPosition.BOTTOM))
    }),
      catchError(error => this.snackBarService.handleApiError(error))
    ))
  }

  deleteDefinitionValue(definitionType: DefinitionType, deleteDefinitionModel: DeleteDefinitionValueDto): Observable<DefinitionModelDto> {
    return browserOnly<DefinitionModelDto>(this.platformId, {} as DefinitionModelDto, () => this.http.delete<never>(`${this.apiUrl}/definition/delete/${definitionType}/${deleteDefinitionModel.id}`).pipe(tap(() => {
      this.snackBarService.showResponse("Definitionsvärde borttagen.", SnackbarConfig.combine(SnackbarType.SUCCESS, SnackbarPosition.BOTTOM))
    }),
      catchError(error => this.snackBarService.handleApiError(error))
    ))
  }

  deleteMachineManufacturerModelValue(deleteMachineMaufacturerModel: DeleteMachineManufacturerModelDto): Observable<MachineManufacturerModelDto> {
    return browserOnly<MachineManufacturerModelDto>(this.platformId, {} as MachineManufacturerModelDto, () => this.http.delete<never>(`${this.apiUrl}/machinemanufacturermodel/delete/${deleteMachineMaufacturerModel.id}`).pipe(tap(() => {
      this.snackBarService.showResponse("Maskintillverkarmodell borttagen.", SnackbarConfig.combine(SnackbarType.SUCCESS, SnackbarPosition.BOTTOM))
    }),
      catchError(error => this.snackBarService.handleApiError(error))
    ))
  }
}
